import type { FilterFn } from "@tanstack/react-table";

import { Typo } from "@pepperstone/pepper-ui-kit";
import { createColumnHelper } from "@tanstack/react-table";
import dayjs from "dayjs";

import type { LeadV2, LeadV2Status } from "../../api/generated";
import type { DateValueType } from "../../DatepickerV2";
import type { ColumnLabels } from "../types";

import { createBackofficeFieldDecorators } from "../../ClientPage/utils/redactedFieldsHelper";
import { LeadStatusDisplay } from "../../LeadStatus";
import { formatEpochToLocaleDate } from "../../utils/date";

export type TLead = LeadV2 & {
  tStatus: string;
  tCountry: string;
  tDate: number;
};

export const columnSortingMapping: Record<string, string> = {
  name: "name",
  email: "email",
  clientId: "clientId",
  tDate: "createdAt",
  tStatus: "status",
  tCountry: "country",
};

export const transformStatus = (status: LeadV2Status): string => {
  switch (status) {
    case "IdentifyVerification":
      return "Identity Verification";

    default:
      return status.replace(/([a-z])([A-Z])/g, "$1 $2");
  }
};

const columnHelper = createColumnHelper<TLead>();

const HeaderTypo = ({ text }: { text?: string }) => (
  <Typo elem="p" size="sm" type="body">
    {text}
  </Typo>
);

const BodyTypo = ({ text }: { text: string }) => (
  <Typo elem="p" size="md" type="body" clamp="ps-line-clamp-1">
    {text}
  </Typo>
);

export const fuzzyFilter: FilterFn<any> = (
  row,
  columnId,
  value: DateValueType,
) => {
  if (!value?.startDate || !value?.endDate) return true;

  const cellValue = row.getValue<string>(columnId);
  if (!cellValue) return false;

  const date = dayjs(cellValue, "DD MMM YYYY");
  const startDate = dayjs(value.startDate);
  const endDate = dayjs(value.endDate);

  return date.isAfter(startDate) && date.isBefore(endDate);
};

export const customGlobalFilter: FilterFn<any> = (
  row,
  columnId,
  filterValue: string,
) => {
  const search = filterValue.toLowerCase();
  const rowValue = row.getValue<string | null>(columnId);
  if (!rowValue) return false;

  return Boolean(rowValue?.toString()?.toLowerCase()?.includes(search));
};

export const leadColumns = ({
  columnLabels,
  language,
  redactedFields = [],
  isBackoffice = false,
  trackTooltipEvent,
}: {
  columnLabels?: ColumnLabels;
  redactedFields?: string[];
  language: string;
  isBackoffice?: boolean;
  trackTooltipEvent?: (status: LeadV2Status, isMobile: boolean) => void;
}) => {
  const { hiddenColumns, decorateTitle, getRedactedClassName } =
    createBackofficeFieldDecorators(redactedFields, isBackoffice);
  const columns = [
    columnHelper.accessor("name", {
      header: () => (
        <HeaderTypo
          text={decorateTitle("name", columnLabels?.nameColumnLabel)}
        />
      ),
      cell: (info) => <BodyTypo text={info.getValue()} />,
      sortingFn: "alphanumeric",
      size: isBackoffice ? 150 : 315,
      enableGlobalFilter: true,
      meta: { ...getRedactedClassName("name") },
    }),
    columnHelper.accessor("email", {
      header: () => (
        <HeaderTypo
          text={decorateTitle("email", columnLabels?.emailColumnLabel)}
        />
      ),
      cell: (info) => <BodyTypo text={info.getValue()} />,
      sortingFn: "alphanumeric",
      size: isBackoffice ? 150 : 315,
      enableGlobalFilter: true,
      meta: { ...getRedactedClassName("email") },
    }),
    columnHelper.accessor("clientId", {
      header: () => (
        <HeaderTypo
          text={decorateTitle("clientId", columnLabels?.clientIdColumnLabel)}
        />
      ),
      cell: (info) => <BodyTypo text={info.getValue()} />,
      sortingFn: "alphanumeric",
      size: isBackoffice ? 150 : 315,
      enableGlobalFilter: true,
      meta: { ...getRedactedClassName("clientId") },
    }),
    columnHelper.accessor("tCountry", {
      header: () => <HeaderTypo text={columnLabels?.countryColumnLabel} />,
      cell: (info) => <BodyTypo text={info.getValue()} />,
      sortingFn: "alphanumeric",
      size: 250,
      enableGlobalFilter: false,
    }),
    columnHelper.accessor("tDate", {
      header: () => <HeaderTypo text={columnLabels?.createdColumnLabel} />,
      cell: (info) => (
        <BodyTypo text={formatEpochToLocaleDate(info.getValue(), language)} />
      ),
      filterFn: fuzzyFilter,
      enableGlobalFilter: false,
      enableColumnFilter: true,
      sortingFn: "alphanumeric",
      sortDescFirst: false,
      size: 180,
    }),
    columnHelper.accessor("tStatus", {
      header: () => <HeaderTypo text={columnLabels?.statusColumnLabel} />,
      cell: (info) => (
        <LeadStatusDisplay
          id={info.row.original.clientId}
          status={info.row.original.status}
          displayValue={info.getValue() as string}
          onHover={trackTooltipEvent}
        />
      ),
      filterFn: (row, _columnId, filterValue: string[]) => {
        if (!filterValue?.length) return false;
        const status = row.getValue(_columnId) as string;
        return filterValue.includes(status);
      },
      enableColumnFilter: true,
      sortingFn: "alphanumeric",
      size: 225,
      enableGlobalFilter: false,
    }),
  ];

  return columns.filter((column) => {
    return !hiddenColumns.includes(column.accessorKey);
  });
};
